/**
 * Auto-balanced sliders — pure math
 * For Media Devoted | Created By Ciro Autiero
 *
 * Group weight sliders always sum to exactly 100. The final normalize + round
 * step is the SHARED `normalizeWeightsToHundred` (the same function the backend
 * score engine uses), so the slider a user sees and the weight the optimizer
 * scores with can never disagree. This file only adds the interactive "move
 * one slider, redistribute the delta across the others" step on top of it.
 */

import { normalizeWeightsToHundred } from '@trafficpulse/shared';
import type { FlowLayer } from '@trafficpulse/shared';

export interface RebalanceMove {
  id: string;
  target: number;
}

/**
 * Group weights for every layer. Each layer's map holds ONLY the groups that
 * score that layer and always sums to exactly 100 independently of the others.
 */
export type PerLayerWeights = Record<FlowLayer, Record<string, number>>;

/** Rebalance ONE layer's weights (optionally applying a user move) to sum 100. */
export function rebalanceLayer(
  perLayer: PerLayerWeights,
  layer: FlowLayer,
  move: RebalanceMove | null,
): PerLayerWeights {
  return { ...perLayer, [layer]: rebalance(perLayer[layer] ?? {}, move) };
}

/** Rebalance every layer's weights to sum 100 (no user move). */
export function rebalanceAllLayers(perLayer: PerLayerWeights): PerLayerWeights {
  return {
    paths: rebalance(perLayer.paths ?? {}, null),
    landers: rebalance(perLayer.landers ?? {}, null),
    offers: rebalance(perLayer.offers ?? {}, null),
  };
}

export function rebalance(
  values: Record<string, number>,
  move: RebalanceMove | null,
): Record<string, number> {
  const ids = Object.keys(values);
  if (ids.length === 0) return {};
  const next = move ? applyMove(values, move) : values;
  return normalizeWeightsToHundred(next, ids);
}

function applyMove(
  values: Record<string, number>,
  move: RebalanceMove,
): Record<string, number> {
  const ids = Object.keys(values);
  const otherIds = ids.filter((id) => id !== move.id);
  if (otherIds.length === 0) return { [move.id]: 100 };
  const sumOthers = otherIds.reduce((s, id) => s + (values[id] ?? 0), 0);
  const target = clampPercent(move.target);
  const otherBudget = 100 - target;
  const result: Record<string, number> = { [move.id]: target };
  if (sumOthers <= 0) {
    const each = otherBudget / otherIds.length;
    for (const id of otherIds) result[id] = each;
    return result;
  }
  const factor = otherBudget / sumOthers;
  for (const id of otherIds) result[id] = (values[id] ?? 0) * factor;
  return result;
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}
